import React from "react";
import { Trash, X } from "@phosphor-icons/react";
import { Button } from "@shopflo/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shopflo/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@shopflo/ui/sheet";
import { Icon } from "@shopflo/ui/icon";
import { IconButton } from "@shopflo/ui/icon-button";
import { useLocale } from "lib/hooks/useLocale";
import { isMobile } from "lib/utils/helpers";

export interface DeleteConfirmationPopupProps {
  isOpen: boolean;
  actions: {
    onConfirm: () => void;
    onClose: () => void;
  };
  config: {
    cancelLabel?: string;
    confirmationText?: string;
    description: string;
    isLoading?: boolean;
    title: string;
  };
  /** Mount inside checkout iframe root (embedded widget). */
  portalContainer?: HTMLElement | null;
}

const TrashIcon = () => (
  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-50">
    <Icon IconComponent={Trash} size="md" className="text-red-600" iconProps={{ weight: "duotone" }} />
  </div>
);

const DeleteConfirmationPopup = ({
  isOpen,
  actions,
  config: {
    cancelLabel,
    confirmationText,
    description,
    isLoading = false,
    title,
  },
  portalContainer,
}: DeleteConfirmationPopupProps) => {
  const { onConfirm, onClose } = actions;
  const { t } = useLocale();

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const outlineLabel = cancelLabel ?? t("cancel");

  const confirmationHeaderSectionClassName =
    "relative flex flex-col items-center gap-4 px-5 pb-0 pt-4 text-center sm:text-center";

  const confirmationFooter = (
    <div className="flex gap-2 px-5 pb-4 pt-0">
      <Button variant="outline" onClick={onClose} size="md" className="w-full">
        {outlineLabel}
      </Button>
      <Button variant="error" size="md" onClick={onConfirm} className="w-full" loading={isLoading}>
        {confirmationText ?? "Yes, Proceed"}
      </Button>
    </div>
  );

  const shouldRenderBottomSheet =
    isMobile() || (typeof window !== "undefined" && window.innerWidth <= 640);

  if (shouldRenderBottomSheet) {
    return (
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          className="relative h-auto !rounded-t-2xl !border-0 bg-white !p-0 pb-[env(safe-area-inset-bottom,0)]"
          overlayClassName="!bg-black/40"
          withinContainer={Boolean(portalContainer)}
          container={portalContainer ?? undefined}>
          <div className="absolute top-[12px] right-[12px] z-10 w-fit">
            <IconButton
              type="button"
              IconComponent={X}
              size="sm"
              variant="ghost"
              className="w-fit text-zinc-500"
              onClick={onClose}
              aria-label="Close"
              iconProps={{ weight: "regular" }}
            />
          </div>
          <SheetHeader className="sr-only">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>{description}</SheetDescription>
          </SheetHeader>
          <div className={confirmationHeaderSectionClassName}>
            <div className="flex w-full justify-center mt-4">
              <TrashIcon />
            </div>
            <div className="flex w-full flex-col gap-2 mb-4">
              <h2 className="text-text-md-semibold text-zinc-900">{title}</h2>
              <p className="text-text-sm-regular text-zinc-500 px-5">{description}</p>
            </div>
          </div>
          {confirmationFooter}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="relative !max-w-[360px] overflow-hidden !rounded-2xl border border-zinc-200 bg-white !p-0 shadow-[0_20px_40px_rgba(16,24,40,0.12),0_8px_16px_rgba(16,24,40,0.08)] [&>button:last-of-type]:hidden"
        withinContainer={Boolean(portalContainer)}
        container={portalContainer ?? undefined}>
        <div className="absolute top-[12px] right-[12px] z-10 w-fit">
          <IconButton
            type="button"
            IconComponent={X}
            size="sm"
            variant="ghost"
            className="w-fit text-zinc-500"
            onClick={onClose}
            aria-label="Close"
            iconProps={{ weight: "regular" }}
          />
        </div>
        <DialogHeader className={confirmationHeaderSectionClassName}>
          <div className="flex w-full justify-center">
            <TrashIcon />
          </div>
          <div className="flex w-full flex-col gap-2">
            <DialogTitle className="!text-text-md-semibold text-zinc-900 text-center !mt-0">{title}</DialogTitle>
            <DialogDescription className="!text-text-sm-regular text-zinc-500 !mt-0">
              {description}
            </DialogDescription>
          </div>
        </DialogHeader>
        {confirmationFooter}
      </DialogContent>
    </Dialog>
  );
};

export default DeleteConfirmationPopup;
